import { useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import * as XLSX from 'xlsx';
import { tenantAPI, type Zone } from '@vehix/portal/services/api/tenants';
import { useToast } from '@vehix/shared/admin/portal-bridge';
import { zoneKeys } from './useZones';

/**
 * Multi-zone ZIP-code export hook used by the Zone Map / Presentation mode
 * "Download ZIPs" button. Produces a single .xlsx workbook with one sheet
 * per zone. Reuses the same React Query cache key
 * ({@link zoneKeys.zipCodes}) as the single-zone export in Zone List, so
 * any zone's ZIPs already fetched in the last 15 minutes short-circuit to
 * cached data instead of re-querying.
 *
 * Sheet names are sanitized to satisfy Excel's constraints: max 31 chars
 * and no `\ / ? * [ ] :` characters. Without sanitization, long or
 * punctuation-heavy zone names crash the export with an opaque XLSX error.
 */
function sanitizeSheetName(raw: string, fallback: string): string {
  const cleaned = raw.replace(/[\\\/\?\*\[\]:]/g, '_').trim();
  const safe = cleaned.length > 0 ? cleaned : fallback;
  return safe.slice(0, 31);
}

export function useMultiZoneZipExport() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [isExportingZips, setIsExportingZips] = useState(false);

  const exportZips = useCallback(
    async (zones: Zone[]) => {
      if (zones.length === 0) {
        toast({
          title: 'No zones selected',
          description: 'Pick one or more zones before exporting.',
          variant: 'destructive'
        });
        return;
      }

      setIsExportingZips(true);
      try {
        const results = await Promise.all(
          zones.map(async zone => {
            const zipCodes = await queryClient.fetchQuery({
              queryKey: zoneKeys.zipCodes(zone.id),
              queryFn: () => tenantAPI.getZoneZipCodes(zone.id),
              staleTime: 15 * 60 * 1000
            });
            return { zone, zipCodes };
          })
        );

        const workbook = XLSX.utils.book_new();
        const usedSheetNames = new Set<string>();
        results.forEach(({ zone, zipCodes }, index) => {
          const baseName = sanitizeSheetName(
            zone.zone_slug || zone.zone_name || '',
            `Zone ${index + 1}`
          );
          // Disambiguate collisions (two zones with very similar long names
          // can collide after the 31-char truncation).
          let sheetName = baseName;
          let suffix = 2;
          while (usedSheetNames.has(sheetName)) {
            const tail = ` (${suffix})`;
            sheetName = `${baseName.slice(0, 31 - tail.length)}${tail}`;
            suffix += 1;
          }
          usedSheetNames.add(sheetName);

          const rows = [
            { 'Zone Name': zone.zone_name || '', 'Zip Codes': zipCodes.join(', ') }
          ];
          const worksheet = XLSX.utils.json_to_sheet(rows);
          worksheet['!cols'] = [{ wch: 28 }, { wch: 100 }];
          XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
        });

        const totalZips = results.reduce((sum, r) => sum + r.zipCodes.length, 0);
        const today = new Date().toISOString().slice(0, 10);
        const filename = `vehix-zone-zips-${today}-${zones.length}zones.xlsx`;
        XLSX.writeFile(workbook, filename);

        toast({
          title: 'Export successful',
          description: `Exported ${totalZips} ZIP codes across ${zones.length} zone${
            zones.length === 1 ? '' : 's'
          }.`
        });
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Unknown error';
        toast({
          title: 'Export failed',
          description: message,
          variant: 'destructive'
        });
      } finally {
        setIsExportingZips(false);
      }
    },
    [queryClient, toast]
  );

  return { exportZips, isExportingZips };
}
